<template>
  <div class="trips-tab">
    <div v-if="!tableOnly" class="view-switch">
      <label class="pct-toggle">
        <input type="checkbox" v-model="statusPct" />
        в %
      </label>
    </div>

    <div v-if="!tableOnly" class="chart-block">
      <div class="chart-title">Распределение статусов по дням</div>
      <VChart v-if="dayRows.length" class="chart" :option="statusOption" autoresize />
      <div v-else-if="dayLoading" class="chart-empty">Загрузка…</div>
      <div v-else class="chart-empty">Нет данных по выбранным фильтрам</div>
    </div>

    <my-loader v-if="isLoading && !rows.length" />
    <template v-else>
      <div class="table-scroll">
        <table class="stat-table" v-if="rows.length">
          <thead>
            <tr v-for="(headerGroup, gi) in table.getHeaderGroups()" :key="headerGroup.id">
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :title="columnHint(header.column.id, header.column.columnDef.header)"
                :class="{
                  'th-group': isGroupHeader(header),
                  'th-empty': gi === 0 && table.getHeaderGroups().length > 1 && !isGroupHeader(header),
                  'th-hl': isHlColumn(header.column.id),
                }"
              >
                <template v-if="gi === 0 && table.getHeaderGroups().length > 1">
                  <span v-if="isGroupHeader(header)">{{ header.column.columnDef.header }}</span>
                </template>
                <button
                  v-else-if="header.column.getCanSort()"
                  class="th-sort"
                  type="button"
                  :class="{ active: header.column.getIsSorted() }"
                  @click="header.column.getToggleSortingHandler()($event)"
                >
                  {{ header.column.columnDef.header }}
                  <span class="sort-mark">{{ sortMark(header.column.id) }}</span>
                </button>
                <span v-else>{{ header.column.columnDef.header }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in table.getRowModel().rows"
              :key="row.id"
              :class="{ 'row-new': !row.original.has_erp_pair, 'row-clickable': canTrip360 }"
              @click="canTrip360 && openTrip360(row.original)"
            >
              <td v-for="cell in row.getVisibleCells()" :key="cell.id" :class="{ 'cell-hl': isHlColumn(cell.column.id) }">
                <template v-if="cell.column.id === 'distr_id'">
                  <span class="id-cell">{{ row.original.distr_id }}</span>
                  <span v-if="!row.original.has_erp_pair" class="new-badge" title="Рейс ещё не заведён в ERP">новый</span>
                </template>
                <template v-else-if="cell.column.id === 'status_name'">
                  <span class="status-pill" :class="statusClass(row.original.status_name)">
                    {{ row.original.status_name }}
                  </span>
                </template>
                <template v-else-if="cell.column.id === 'client_category'">
                  <span
                    v-if="clientCategoryCls(row.original.client_category)"
                    class="cat-badge"
                    :class="clientCategoryCls(row.original.client_category)"
                    title="Категория клиента справочника (штраф за срыв: ТОП 100%, КА 25%)"
                  >{{ clientCategoryLabel(row.original.client_category) }}</span>
                  <span v-else>—</span>
                </template>
                <template v-else-if="cell.column.id === 'win_is_bot'">
                  <span v-if="row.original.win_is_bot === true" class="win-pill win-bot">Бот</span>
                  <span v-else-if="row.original.win_is_bot === false && row.original.win_has_bid" class="win-pill win-human">Человек</span>
                  <span v-else>—</span>
                </template>
                <template v-else-if="cell.column.id === 'timeline'">
                  <div
                    v-if="timelineModel(row.original)"
                    class="tl-widget"
                    :title="timelineModel(row.original).tooltip"
                  >
                    <div class="tl-track">
                      <div
                        class="tl-bar"
                        :class="timelineModel(row.original).barCls"
                        :style="{ width: timelineModel(row.original).barPct + '%' }"
                      ></div>
                    </div>
                    <div
                      v-if="timelineModel(row.original).markPct != null"
                      class="tl-mark"
                      :style="{ left: timelineModel(row.original).markPct + '%' }"
                    ></div>
                    <div
                      v-if="timelineModel(row.original).mark12Pct != null"
                      class="tl-mark tl-mark-12"
                      :style="{ left: timelineModel(row.original).mark12Pct + '%' }"
                    ></div>
                  </div>
                  <span v-else>—</span>
                </template>
                <template v-else-if="cell.column.id === 'base_cost_rub'">
                  <span :title="baseTitle(row.original)">{{ pickBase(row.original) }}</span>
                </template>
                <template v-else-if="cell.column.id === 'fact_cost_rub'">
                  <span
                    class="carrier-cell"
                    :title="row.original.price_x1000_fixed ? 'Цена была введена в рублях вместо тысяч — нормализована (÷1000)' : undefined"
                  >
                    <span>{{ cell.getValue() }}</span>
                    <span v-if="row.original.price_x1000_fixed" class="x1000-mark">÷1k</span>
                  </span>
                </template>
                <template v-else-if="cell.column.id === 'approved_cost'">
                  <span :title="approvedTitle(row.original)">{{ pickApproved(row.original) }}</span>
                </template>
                <template v-else-if="cell.column.id === 'manual_cost'">
                  <span :title="manualTitle(row.original)">{{ pickManual(row.original) }}</span>
                </template>
                <template v-else-if="cell.column.id === 'carrier_price_source'">
                  <span
                    v-if="row.original.carrier_price_source"
                    class="src-tag"
                    :class="`src-${row.original.carrier_price_source}`"
                    :title="sourceTitle(row.original.carrier_price_source)"
                  >{{ sourceLabel(row.original.carrier_price_source) }}</span>
                  <span v-else>—</span>
                </template>
                <template v-else-if="cell.column.id === 'carrier_price_eff'">
                  <span class="carrier-cell">{{ cell.getValue() }}</span>
                </template>
                <template v-else-if="cell.column.id === 'base_deviation'">
                  <span
                    v-if="baseDeviation(row.original) != null"
                    class="dev"
                    :class="devClass(row.original)"
                  >{{ cell.getValue() }}</span>
                  <span v-else class="dev dev-zero">0,0%</span>
                </template>
                <template v-else-if="cell.column.id === 'autoleader'">
                  <span
                    :title="autoleaderTitle(row.original)"
                  >{{ cell.getValue() }}</span>
                </template>
                <template v-else-if="cell.column.id === 'created_at'">
                  <span :title="row.original.has_erp_pair ? 'Заведено в ERP' : 'Заведено в Пчёлку'">
                    {{ createdLabel(row.original) }}
                  </span>
                </template>
                <template v-else>{{ cell.getValue() }}</template>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!rows.length" class="empty-table">Нет рейсов по заданным фильтрам</div>
      </div>
      <div class="table-footer">
        <span class="total-info">{{ fmtNum(total) }} рейсов</span>
        <my-pagination
          :total-pages="totalPages"
          :current-page="page"
          @page-changed="onPageChanged"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { getCoreRowModel, useVueTable } from '@tanstack/vue-table';
import { useTrip360Store } from '@/stores/trip360';
import { searchRowKey } from '@/components/trip360/trip360Logic.mjs';
import VChart from '@/components/charts/echarts';
import { fmtDate, fmtHoursNum, fmtMoney, fmtNum, fmtPct } from '../format';
import { closeTimelineModel as timelineModel } from '../closeTimeline';

const STATUS_SERIES = [
  { field: 'opened', label: 'Открыт', color: '#3b82f6' },
  { field: 'closed', label: 'Закрыт', color: '#12b76a' },
  { field: 'canceled', label: 'Отменен', color: '#98a2b3' },
  { field: 'deleted', label: 'Удален', color: '#d0d5dd' },
  { field: 'outdated', label: 'Устарел', color: '#667085' },
  { field: 'breakdown', label: 'Срыв', color: '#f04438' },
  { field: 'refusal', label: 'Отказ', color: '#f79009' },
  { field: 'no_card', label: 'Без карточки', color: '#e4e7ec' },
];

export default {
  name: 'TripsTab',
  components: { VChart },
  props: {
    rows: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 50 },
    sorting: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    dayRows: { type: Array, default: () => [] },
    dayLoading: { type: Boolean, default: false },
    // Режим «только таблица» (напр., вкладка «Рейсы» в конструкторе):
    // без графика статусов и переключателя «в %»
    tableOnly: { type: Boolean, default: false },
  },
  emits: ['sortingChanged', 'pageChanged'],
  setup(props, { emit }) {
    const store = useStore();
    const trip360 = useTrip360Store();

    const canTrip360 = computed(() => store.getters.hasPermission('trip360.view'));
    const openTrip360 = (row) => {
      const key = searchRowKey(row);
      if (key) trip360.openTrip360(key);
    };

    const COLUMN_HINTS = {
      search_duration_sec: 'От входа в карточку исполнителя, который в итоге закрыл рейс, до закрытия поиска (победы)',
      ongoing_search_sec: 'Только для открытых рейсов: сколько текущий исполнитель уже в карточке на момент обновления данных',
      total_time_in_work_sec: 'Суммарное время всех участников карточки в статусе «в работе»',
      days_to_load_at_close: 'Запас между закрытием поиска и плановой датой погрузки',
      created_lead_hours: 'Запас от создания карточки до плановой погрузки — насколько заранее заведён рейс',
      base_deviation: 'Отклонение расчётной цены перевозчика от базовой цены (порог подсветки ±10%)',
    };
    const columnHint = (id, fallback) => COLUMN_HINTS[id] || fallback;

    const money = (key) => (row) => fmtMoney(row[key]);
    const pct = (key) => (row) => fmtPct(row[key]);
    const hours = (key) => (row) => fmtHoursNum(row[key]);

    // epoch-ms (pandas to_json), epoch-s либо ISO-строка → timestamp в мс
    const toTs = (value) => {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'number') {
        const ms = value > 1e11 ? value : value * 1000;
        return Number.isFinite(ms) ? ms : null;
      }
      const ts = new Date(value).getTime();
      return Number.isNaN(ts) ? null : ts;
    };

    const columns = [
      { id: 'stat_date', header: 'Дата', accessorFn: (row) => fmtDate(row.stat_date), size: 80 },
      { id: 'distr_id', header: '№', accessorFn: (row) => row.distr_id, size: 90 },
      { id: 'display_name', header: 'Платформа', accessorFn: (row) => row.display_name || '—', size: 130 },
      { id: 'segment', header: 'Сегмент', accessorFn: (row) => row.segment || '—', size: 100 },
      {
        id: 'client_category',
        header: 'Категория',
        accessorFn: (row) => clientCategoryLabel(row.client_category),
        size: 105,
      },
      {
        id: 'win_is_bot',
        header: 'Победа',
        accessorFn: (row) => (row.win_is_bot ? 'Бот' : 'Человек'),
        size: 100,
      },
      { id: 'status_name', header: 'Статус', accessorFn: (row) => row.status_name || '—', size: 90 },
      { id: 'timeline', header: 'Закрытие', accessorFn: () => '', enableSorting: false, size: 130 },
      { id: 'city_out', header: 'Откуда', accessorFn: (row) => row.city_out || '—', size: 130 },
      { id: 'city_in', header: 'Куда', accessorFn: (row) => row.city_in || '—', size: 130 },
      { id: 'logist', header: 'Ответственный', accessorFn: (row) => row.logist || '—', size: 150 },
      {
        header: 'Цены перевозчику',
        columns: [
          { id: 'base_cost_rub', header: 'Базовый', accessorFn: (row) => pickBase(row), size: 105 },
          { id: 'approved_cost', header: 'Согласов.', accessorFn: (row) => pickApproved(row), enableSorting: false, size: 105 },
          { id: 'manual_cost', header: 'Ручная', accessorFn: (row) => pickManual(row), enableSorting: false, size: 105 },
          { id: 'fact_cost_rub', header: 'Факт', accessorFn: money('fact_cost_rub'), size: 105 },
        ],
      },
      { id: 'carrier_price_eff', header: 'Ставка пер., ₽', accessorFn: money('carrier_price_eff'), size: 115 },
      { id: 'carrier_price_source', header: 'Источник', enableSorting: false, accessorFn: (row) => sourceLabel(row.carrier_price_source), size: 100 },
      { id: 'base_deviation', header: 'Откл. от базы', enableSorting: false, accessorFn: (row) => devText(row), size: 110 },
      { id: 'customer_gross', header: 'Ставка зак. (с НДС), ₽', accessorFn: money('customer_gross'), size: 130 },
      { id: 'margin_pct', header: 'Маржа', accessorFn: pct('margin_pct'), size: 85 },
      { id: 'profitability_pct', header: 'Рентаб.', accessorFn: pct('profitability_pct'), size: 85 },
      { id: 'autoleader', header: 'Автолидер, ₽', accessorFn: money('autoleader'), size: 115 },
      { id: 'kpi_amount', header: 'KPI, ₽', accessorFn: money('kpi_amount'), size: 100 },
      { id: 'commission_rub', header: 'Ожид. комиссия, ₽', accessorFn: money('commission_rub'), size: 130 },
      { id: 'net_commission_rub', header: 'Чистая, ₽', accessorFn: money('net_commission_rub'), size: 110 },
      { id: 'breakdown_loss_rub', header: 'Штрафы срывов, ₽', accessorFn: money('breakdown_loss_rub'), size: 115 },
      { id: 'kpi_fund_put_rub', header: 'В фонд, ₽', accessorFn: money('kpi_fund_put_rub'), size: 105 },
      { id: 'kpi_fund_take_rub', header: 'Из фонда, ₽', accessorFn: money('kpi_fund_take_rub'), size: 105 },
      { id: 'financial_result_rub', header: 'Финрез., ₽', accessorFn: money('financial_result_rub'), size: 110 },
      { id: 'search_duration_sec', header: 'Поиск, ч.', accessorFn: hours('search_duration_sec'), size: 90 },
      { id: 'ongoing_search_sec', header: 'В поиске, ч.', accessorFn: hours('ongoing_search_sec'), size: 90 },
      { id: 'total_time_in_work_sec', header: 'В работе, ч.', accessorFn: hours('total_time_in_work_sec'), size: 90 },
      {
        id: 'days_to_load_at_close',
        header: 'До погрузки, ч.',
        accessorFn: (row) => fmtHoursNum(row.days_to_load_at_close == null ? null : row.days_to_load_at_close * 24 * 3600),
        size: 105,
      },
      {
        id: 'created_lead_hours',
        header: 'Создан заранее, ч.',
        enableSorting: false,
        accessorFn: (row) => {
          const start = toTs(row.card_create_dt);
          const load = toTs(row.departure_planed_dt);
          if (start == null || load == null) return '—';
          return ((load - start) / (60 * 60 * 1000)).toLocaleString('ru-RU', { maximumFractionDigits: 1 });
        },
        size: 120,
      },
      {
        id: 'created_at',
        header: 'Заведено',
        accessorFn: (row) => (row.has_erp_pair ? fmtDate(row.erp_created_dt) : fmtDate(row.card_create_dt)),
        enableSorting: false,
        size: 110,
      },
      { id: 'work_status_name', header: 'ERP статус', accessorFn: (row) => row.work_status_name || '—', enableSorting: false, size: 110 },
    ];

    const table = useVueTable({
      get data() { return props.rows; },
      columns,
      getCoreRowModel: getCoreRowModel(),
      manualSorting: true,
      manualPagination: true,
      state: {
        get sorting() { return props.sorting; },
      },
      onSortingChange: (updater) => {
        const next = typeof updater === 'function' ? updater(props.sorting) : updater;
        emit('sortingChanged', next);
      },
    });

    const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)));
    const isGroupHeader = (header) => Array.isArray(header.column.columns) && header.column.columns.length > 0;
    const sortMark = (id) => {
      const entry = props.sorting.find((item) => item.id === id);
      if (!entry) return '';
      return entry.desc ? '↓' : '↑';
    };
    const onPageChanged = (page) => emit('pageChanged', page);

    const statusPct = ref(false);

    const statusOption = computed(() => {
      const days = props.dayRows.map((row) => fmtDate(row.day));
      const series = STATUS_SERIES.map((status) => ({
        name: status.label,
        type: 'bar',
        stack: 'statuses',
        data: props.dayRows.map((row) => Number(row[status.field]) || 0),
        itemStyle: { color: status.color },
        barMaxWidth: 28,
      }));
      if (statusPct.value) {
        const totals = props.dayRows.map((row) => STATUS_SERIES.reduce(
          (acc, s) => acc + (Number(row[s.field]) || 0), 0
        ));
        series.forEach((s) => {
          s.data = s.data.map((v, idx) => (totals[idx] ? (v / totals[idx]) * 100 : 0));
        });
      }
      return {
        grid: { left: 8, right: 16, top: 32, bottom: 8, containLabel: true },
        legend: {
          top: 0,
          textStyle: { fontSize: 11, color: '#667085' },
          type: 'scroll',
        },
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: { type: 'shadow' },
          valueFormatter: statusPct.value
            ? (v) => `${(Number(v) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`
            : (v) => fmtNum(v),
        },
        xAxis: { type: 'category', data: days, axisLabel: { rotate: 45, fontSize: 10 } },
        yAxis: statusPct.value
          ? { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } }
          : { type: 'value', minInterval: 1 },
        series,
      };
    });

    const STATUS_STYLES = {
      'Открыт': 'pill-open',
      'Закрыт': 'pill-closed',
      'Отменен': 'pill-gray',
      'Удален': 'pill-gray',
      'Устарел': 'pill-gray',
      'Срыв': 'pill-red',
      'Отказ': 'pill-orange',
      'Без карточки': 'pill-gray',
    };
    const pickMoney = (v) => (v === null || v === undefined || v !== v ? null : Number(v));
    const pickBase = (row) => fmtMoney(pickMoney(row.base_cost_nds_rub) ?? pickMoney(row.base_cost_rub));
    const pickApproved = (row) =>
      row.approved_confirmed
        ? fmtMoney(pickMoney(row.approved_cost_with_nds_rub) ?? pickMoney(row.approved_cost_without_nds_rub))
        : '—';
    const pickManual = (row) => fmtMoney(pickMoney(row.manual_cost_nds_rub) ?? pickMoney(row.manual_cost_rub));
    const baseTitle = (row) => {
      const nds = pickMoney(row.base_cost_nds_rub);
      const plain = pickMoney(row.base_cost_rub);
      if (nds == null && plain == null) return 'Базовый тариф маршрута (distribution_routes) — нет';
      return `Базовый тариф маршрута (distribution_routes)\nс НДС: ${fmtMoney(nds)}\nбез НДС: ${fmtMoney(plain)}`;
    };
    const approvedTitle = (row) => {
      if (!row.approved_confirmed) return 'Согласованная ставка не подтверждена';
      const nds = pickMoney(row.approved_cost_with_nds_rub);
      const plain = pickMoney(row.approved_cost_without_nds_rub);
      return `Согласованная ставка (approved_result)\nс НДС: ${fmtMoney(nds)}\nбез НДС: ${fmtMoney(plain)}`;
    };
    const manualTitle = (row) => {
      const nds = pickMoney(row.manual_cost_nds_rub);
      const plain = pickMoney(row.manual_cost_rub);
      if (nds == null && plain == null) return 'Ручная ставка из карточки — нет';
      return `Ручная ставка из карточки Пчёлки\nс НДС: ${fmtMoney(nds)}\nбез НДС: ${fmtMoney(plain)}`;
    };
    const baseDeviation = (row) => {
      const base = pickMoney(row.base_cost_nds_rub) ?? pickMoney(row.base_cost_rub);
      const eff = pickMoney(row.carrier_price_eff);
      if (base == null || base <= 0 || eff == null) return null;
      return ((eff - base) / base) * 100;
    };
    const devClass = (row) => {
      const dev = baseDeviation(row);
      if (dev == null) return 'dev-ok';
      if (dev > 10) return 'dev-up';
      if (dev < -10) return 'dev-down';
      return 'dev-ok';
    };
    const fmtDev = (dev) => `${dev > 0 ? '+' : ''}${dev.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
    const devText = (row) => {
      const dev = baseDeviation(row);
      return dev == null ? '—' : fmtDev(dev);
    };

    const fmtHours = (v) => {
      if (v === null || v === undefined || v !== v) return '—';
      return (Number(v) * 24).toLocaleString('ru-RU', { maximumFractionDigits: 1 });
    };
    const HL_COLS = new Set(['carrier_price_eff', 'customer_gross']);
    const isHlColumn = (id) => HL_COLS.has(id);
    const SOURCE_LABELS = {
      fact: 'факт',
      approved: 'соглас.',
      manual: 'ручной',
      base: 'база',
    };
    const SOURCE_TITLES = {
      fact: 'Фактическая ставка закрытия из карточки Пчёлки',
      approved: 'Согласованная ставка (approved_result)',
      manual: 'Ручная ставка из карточки Пчёлки',
      base: 'Базовый тариф маршрута (distribution_routes)',
    };
    const sourceLabel = (source) => SOURCE_LABELS[source] || '—';
    const sourceTitle = (source) => SOURCE_TITLES[source] || '';
    const autoleaderTitle = (row) => {
      const money = (v) => (v === null || v === undefined || v !== v ? '—' : fmtMoney(v));
      return `Автолидер: ${money(row.autoleader)}\nНДС-разрыв: ${money(row.vat_gap)}\nРезерв прибыли: ${money(row.profit_reserve)}\nНалог. фонд: ${money(row.tax_fund)}\nТарифная разница: ${money(row.tariff_difference)}`;
    };
    const statusClass = (status) => STATUS_STYLES[status] || 'pill-gray';
    // Категория клиента (top/key/regular) — как бейджи в распределении
    const CLIENT_CATEGORY_LABELS = { top: 'ТОП', key: 'КА', regular: 'Обычный' };
    const clientCategoryLabel = (value) => CLIENT_CATEGORY_LABELS[value] || '—';
    const clientCategoryCls = (value) => {
      if (value === 'top') return 'cat-top';
      if (value === 'key') return 'cat-key';
      if (value === 'regular') return 'cat-regular';
      return null;
    };
    const createdLabel = (row) => {
      if (row.has_erp_pair) {
        return `${fmtDate(row.erp_created_dt)} · ERP`;
      }
      return `${fmtDate(row.card_create_dt)} · Пчёлка`;
    };

    return {
      table,
      totalPages,
      isGroupHeader,
      sortMark,
      onPageChanged,
      fmtNum,
      statusClass,
      clientCategoryLabel,
      clientCategoryCls,
      createdLabel,
      canTrip360,
      openTrip360,
      sourceLabel,
      sourceTitle,
      autoleaderTitle,
      pickBase,
      pickApproved,
      pickManual,
      baseTitle,
      approvedTitle,
      manualTitle,
      baseDeviation,
      devClass,
      fmtDev,
      columnHint,
      fmtHours,
      isHlColumn,
      timelineModel,
      statusPct,
      statusOption,
    };
  },
};
</script>

<style scoped>
.trips-tab {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.view-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.view-chip {
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #475467;
  border-radius: 14px;
  padding: 4px 14px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.view-chip:hover {
  border-color: #84adff;
  color: #175cd3;
}

.view-chip.active {
  background: #eff6ff;
  border-color: #84adff;
  color: #175cd3;
  font-weight: 600;
}

.pct-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #475467;
  cursor: pointer;
  user-select: none;
}

.chart-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
  margin-bottom: 12px;
  flex: none;
  display: flex;
  flex-direction: column;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 8px;
}

.chart {
  width: 100%;
  height: 300px;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #98a2b3;
  font-size: 14px;
}

.table-scroll {
  flex: 1;
  overflow: auto;
  min-height: 200px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.stat-table {
  border-collapse: collapse;
  font-size: 13px;
  color: #344054;
  white-space: nowrap;
  user-select: none;
}

.stat-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 8px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  color: #475467;
  font-weight: 600;
  text-align: left;
}

.th-sort {
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.th-sort:hover { color: #0056b3; }
.th-sort.active { color: #007bff; }
.sort-mark { font-size: 11px; }

.stat-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f2f4f7;
}

.stat-table tbody tr:hover { background: #f8fafc; }
.row-new { background: #fffbeb; }
.row-new:hover { background: #fef3c7; }
.row-clickable { cursor: pointer; }

.id-cell { font-weight: 600; }

.new-badge {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 8px;
  background: #fef3c7;
  color: #92400e;
  font-size: 11px;
}

.cell-hl {
  background: #eef4ff;
}

.th-hl {
  background: #dce7fb;
}

/* пустые ячейки одиночных колонок в верхнем ряду заголовка */
.th-empty {
  background: #f8fafc;
}

.dev-zero {
  background: #f2f4f7;
  color: #98a2b3;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.pill-open {
  background: #eff6ff;
  color: #1d4ed8;
}

.pill-closed {
  background: #ecfdf3;
  color: #027a48;
}

.pill-gray {
  background: #f2f4f7;
  color: #475467;
}

.pill-red {
  background: #fef3f2;
  color: #b42318;
}

.pill-orange {
  background: #fffaeb;
  color: #b54708;
}

.win-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.win-bot {
  background: #f4f0ff;
  color: #5925dc;
}

.win-human {
  background: #ecfdf3;
  color: #067647;
}

.cat-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.cat-top {
  background: #fff3cd;
  color: #856404;
}

.cat-key {
  background: #cce5ff;
  color: #004085;
}

.cat-regular {
  background: #f2f4f7;
  color: #475467;
}

.carrier-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.src-tag {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.src-fact {
  background: #ecfdf3;
  color: #067647;
}

.src-approved {
  background: #eff8ff;
  color: #175cd3;
}

.src-manual {
  background: #fffaeb;
  color: #b54708;
}

.src-base {
  background: #f2f4f7;
  color: #475467;
}

.x1000-mark {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 8px;
  background: #fef3f2;
  color: #b42318;
  font-size: 11px;
  font-weight: 500;
}

.th-group {
  text-align: center;
  font-size: 12px;
  color: #007bff;
  border-bottom: 1px solid #e5e7eb;
}

.dev {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
}

.dev-ok {
  background: #f2f4f7;
  color: #475467;
}

.dev-up {
  background: #fffaeb;
  color: #b54708;
}

.dev-down {
  background: #fef3f2;
  color: #b42318;
}

.tl-widget {
  position: relative;
  width: 120px;
  height: 14px;
}

.tl-track {
  width: 100%;
  height: 100%;
  border-radius: 3px;
  background: #f2f4f7;
  overflow: hidden;
}

.tl-bar {
  height: 100%;
  border-radius: 3px 0 0 3px;
}

.tl-early { background: #12b76a; color: #12b76a; }
.tl-lastday { background: #f79009; color: #f79009; }
.tl-late { background: #f04438; color: #f04438; }
.tl-neutral { background: #98a2b3; color: #98a2b3; }

.tl-open {
  opacity: .55;
  background: repeating-linear-gradient(45deg, currentColor 0 3px, transparent 3px 6px);
}

.tl-mark {
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 1px;
  background: #98a2b3;
}

.empty-table {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #98a2b3;
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 10px;
}

.total-info {
  color: #6b7280;
  font-size: 13px;
}
</style>
